import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { canManageTenantUsers } from '../utils/roles';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Tenant Users ("Team") page — a Customer Super User manages the staff logins
 * within their own tenant (design §2.6). Backed by /tenant-users (SuperUser or
 * platform-staff only; Editors/Users get 403). Lives inside the tenant hub.
 */

interface TenantUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  mustChangePassword: boolean;
  createdAt: string;
}
interface InviteResult extends TenantUser {
  tempPassword: string;
}

const ROLE_OPTIONS: { value: string; label: string; help: string }[] = [
  { value: 'CustomerSuperUser', label: 'Super User', help: 'Full control, including managing users' },
  { value: 'CustomerEditor', label: 'Editor', help: 'Manage events, guests, seating, tickets, check-in' },
  { value: 'CustomerUser', label: 'User', help: 'Read-only — view guests, seating, reports' },
];

const ROLE_LABEL: Record<string, string> = {
  CustomerSuperUser: 'Super User',
  CustomerEditor: 'Editor',
  CustomerUser: 'User',
  // legacy fallbacks (older tenants provisioned pre-Phase B)
  Organizer: 'Super User (legacy)',
  Admin: 'Platform',
  PlatformAdmin: 'Platform',
  PlatformUser: 'Platform',
};

export function TenantUsersPage() {
  const { user } = useAuth();
  const canManage = canManageTenantUsers(user?.role);
  const isMobile = useIsMobile();

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite form
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('CustomerEditor');
  const [submitting, setSubmitting] = useState(false);
  const [justInvited, setJustInvited] = useState<InviteResult | null>(null);

  // Per-row busy state
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  // Inline edit (name / email) state — one row at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');

  function startEdit(u: TenantUser) {
    setEditingId(u.id);
    setEditName(u.fullName);
    setEditEmail(u.email);
    setError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditEmail('');
  }

  async function handleSaveEdit(u: TenantUser) {
    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();
    if (!name) {
      setError('Name must not be empty.');
      return;
    }
    // Only send email if it actually changed, to avoid needless conflict checks.
    const body: { fullName: string; email?: string } =
      email && email !== u.email.toLowerCase() ? { fullName: name, email } : { fullName: name };
    setRowBusy(u.id);
    setError('');
    try {
      await apiClient(`/tenant-users/${u.id}`, { method: 'PATCH', body });
      cancelEdit();
      await loadUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes');
    } finally {
      setRowBusy(null);
    }
  }

  async function loadUsers() {
    try {
      const data = await apiClient<TenantUser[]>('/tenant-users');
      setUsers(data);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setJustInvited(null);
    try {
      const result = await apiClient<InviteResult>('/tenant-users', {
        method: 'POST',
        body: { email: email.trim(), fullName: fullName.trim(), role },
      });
      setJustInvited(result);
      setEmail('');
      setFullName('');
      setRole('CustomerEditor');
      await loadUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to invite user');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRoleChange(u: TenantUser, nextRole: string) {
    if (nextRole === u.role) return;
    setRowBusy(u.id);
    setError('');
    try {
      await apiClient(`/tenant-users/${u.id}/role`, {
        method: 'PATCH',
        body: { role: nextRole },
      });
      await loadUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to change role');
    } finally {
      setRowBusy(null);
    }
  }

  async function handleRemove(u: TenantUser) {
    if (!window.confirm(`Remove ${u.fullName} (${u.email})? This deletes their login.`)) return;
    setRowBusy(u.id);
    setError('');
    try {
      await apiClient(`/tenant-users/${u.id}`, { method: 'DELETE' });
      await loadUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to remove user');
    } finally {
      setRowBusy(null);
    }
  }

  if (!canManage) {
    return (
      <div style={styles.denied}>
        <h2 style={{ margin: '0 0 8px' }}>Team management</h2>
        <p style={{ color: MUTED }}>
          Only a Super User can manage your team’s users. Ask your account’s Super User for access.
        </p>
      </div>
    );
  }

  const roleEditable = (u: TenantUser) =>
    ['CustomerSuperUser', 'CustomerEditor', 'CustomerUser'].includes(u.role);

  return (
    <div style={styles.page}>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Team</h1>
          <p style={styles.sub}>Manage who can sign in to your workspace and what they can do.</p>
        </div>
        <span style={styles.count}>{users.length} {users.length === 1 ? 'user' : 'users'}</span>
      </div>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {justInvited && (
        <div style={styles.success}>
          <strong>Invited {justInvited.fullName}.</strong> They sign in with{' '}
          <code>{justInvited.email}</code> and the temporary password{' '}
          <code>{justInvited.tempPassword}</code> — they’ll be asked to set a new password on first
          login. <span style={styles.copyNote}>(Copy this now — it won’t be shown again.)</span>
        </div>
      )}

      <div style={isMobile ? { ...styles.grid, gridTemplateColumns: '1fr' } : styles.grid}>
        {/* Users table */}
        <section style={styles.card}>
          {loading ? (
            <div style={styles.muted}>Loading team…</div>
          ) : users.length === 0 ? (
            <div style={styles.muted}>No users yet.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name / Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === user?.sub;
                  const isEditing = editingId === u.id;
                  return (
                    <tr key={u.id}>
                      <td style={styles.td}>
                        {isEditing ? (
                          <div style={styles.editForm}>
                            <input
                              style={styles.editInput}
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Full name"
                              aria-label="Full name"
                              autoFocus
                            />
                            <input
                              style={styles.editInput}
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              placeholder="Email"
                              aria-label="Email"
                            />
                            <div style={styles.editActions}>
                              <button
                                style={styles.saveBtn}
                                onClick={() => handleSaveEdit(u)}
                                disabled={rowBusy === u.id}
                              >
                                {rowBusy === u.id ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                style={styles.cancelBtn}
                                onClick={cancelEdit}
                                disabled={rowBusy === u.id}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={styles.name}>
                              {u.fullName}
                              {isSelf && <span style={styles.youPill}>you</span>}
                            </div>
                            <div style={styles.emailText}>{u.email}</div>
                            {u.mustChangePassword && (
                              <div style={styles.pwNote}>hasn’t set password yet</div>
                            )}
                          </>
                        )}
                      </td>
                      <td style={styles.td}>
                        {roleEditable(u) ? (
                          <select
                            value={u.role}
                            disabled={rowBusy === u.id}
                            onChange={(e) => handleRoleChange(u, e.target.value)}
                            style={styles.roleSelect}
                            aria-label={`Role for ${u.fullName}`}
                          >
                            {ROLE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span style={styles.roleChip}>{ROLE_LABEL[u.role] || u.role}</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        {!isEditing && (
                          <button
                            style={styles.iconBtn}
                            onClick={() => startEdit(u)}
                            disabled={rowBusy === u.id}
                            title="Edit name or email"
                            aria-label={`Edit ${u.fullName}`}
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          style={styles.iconBtn}
                          onClick={() => handleRemove(u)}
                          disabled={rowBusy === u.id || isSelf || isEditing}
                          title={isSelf ? 'You cannot remove your own account' : 'Remove user'}
                          aria-label={`Remove ${u.fullName}`}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {/* Invite form */}
        <section style={{ ...styles.card, ...styles.formCard, ...(isMobile ? { position: 'static' as const, top: 'auto' } : null) }}>
          <h2 style={styles.h2}>Invite a user</h2>
          <form onSubmit={handleInvite}>
            <label style={styles.label}>Full name</label>
            <input
              style={styles.input}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              required
            />

            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@yourcompany.com"
              required
            />

            <label style={styles.label}>Role</label>
            <div style={styles.roleRadios}>
              {ROLE_OPTIONS.map((o) => (
                <label
                  key={o.value}
                  style={{
                    ...styles.roleOption,
                    ...(role === o.value ? styles.roleOptionOn : null),
                  }}
                >
                  <input
                    type="radio"
                    name="role"
                    value={o.value}
                    checked={role === o.value}
                    onChange={() => setRole(o.value)}
                    style={{ marginRight: 8 }}
                  />
                  <span>
                    <span style={styles.roleOptLabel}>{o.label}</span>
                    <span style={styles.roleOptHelp}>{o.help}</span>
                  </span>
                </label>
              ))}
            </div>

            <button type="submit" style={styles.submit} disabled={submitting}>
              {submitting ? 'Inviting…' : 'Send invite'}
            </button>
            <p style={styles.formHint}>
              We generate a temporary password you share with them. They set their own on first login.
            </p>
          </form>
        </section>
      </div>
    </div>
  );
}

const PRIMARY = '#1a6b3c';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const PRIMARY_SOFT = '#e8f3ec';

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto', padding: '8px 4px 40px' },
  headingRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 },
  h1: { fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#0f2d1f' },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0' },
  count: { color: MUTED, fontSize: '0.85rem', whiteSpace: 'nowrap', paddingTop: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 1fr)', gap: 20, alignItems: 'start' },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  formCard: { position: 'sticky', top: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  name: { fontWeight: 700, color: '#0f2d1f', display: 'flex', alignItems: 'center', gap: 8 },
  youPill: { background: PRIMARY_SOFT, color: PRIMARY, borderRadius: 999, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 700 },
  emailText: { color: MUTED, fontSize: '0.82rem' },
  pwNote: { color: '#b45309', fontSize: '0.72rem', marginTop: 4 },
  roleChip: { display: 'inline-block', background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700 },
  roleSelect: { padding: '6px 8px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.85rem', background: '#fff', cursor: 'pointer' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.95rem', borderRadius: 4, lineHeight: 1 },
  editForm: { display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 280 },
  editInput: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: '0.86rem' },
  editActions: { display: 'flex', gap: 8, marginTop: 2 },
  saveBtn: { padding: '6px 14px', background: PRIMARY, color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '6px 14px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 7, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  h2: { fontSize: '1.05rem', fontWeight: 700, margin: '0 0 14px', color: '#0f2d1f' },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.9rem' },
  roleRadios: { display: 'flex', flexDirection: 'column', gap: 8 },
  roleOption: { display: 'flex', alignItems: 'flex-start', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer' },
  roleOptionOn: { borderColor: PRIMARY, background: PRIMARY_SOFT },
  roleOptLabel: { display: 'block', fontWeight: 700, fontSize: '0.86rem', color: '#0f2d1f' },
  roleOptHelp: { display: 'block', fontSize: '0.76rem', color: MUTED, marginTop: 2 },
  submit: { width: '100%', marginTop: 16, padding: '11px 0', background: PRIMARY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer' },
  formHint: { fontSize: '0.76rem', color: MUTED, marginTop: 10, lineHeight: 1.4 },
  muted: { color: MUTED, padding: '20px 4px', textAlign: 'center' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, cursor: 'pointer' },
  success: { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46', borderRadius: 8, padding: '10px 14px', marginBottom: 16, lineHeight: 1.5 },
  copyNote: { fontStyle: 'italic' },
  denied: { maxWidth: 600, margin: '40px auto', textAlign: 'center', padding: 20 },
};
